import React, { useState, useEffect } from 'react'
import styles from './CareerModal.module.scss'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import Select from 'react-select'

const CareerModal = ({ isModalOpen, onFinish, onReturn, initialCareers }) => {
	const [isAlertMessageVisible, setIsAlertMessageVisible] = useState(false)
	const [careers, setCareers] = useState(() => {
		if (initialCareers && initialCareers.length > 0) {
			return initialCareers.map((career, index) => ({
				...career,
				id:
					career.id && typeof career.id === 'number' && !isNaN(career.id)
						? career.id
						: index + 1,
				tournament_date: career.tournament_date
					? new Date(career.tournament_date).toISOString().split('T')[0]
					: '',
			}))
		}
		return [
			{
				id: 1,
				tournament_name: '',
				tournament_date: '',
				tournament_rank: '',
				league: '',
				is_representative: false,
			},
		]
	})

	useEffect(() => {
		let timer

		if (isAlertMessageVisible) {
			timer = setTimeout(() => {
				setIsAlertMessageVisible(false)
			}, 3000)
		}

		return () => {
			clearTimeout(timer)
		}
	}, [isAlertMessageVisible])

	if (isModalOpen === false) {
		return null
	}

	const handleCareerChange = (id, field, value) => {
		setCareers((prevCareers) =>
			prevCareers.map((career) => {
				if (career.id === id) {
					return { ...career, [field]: value }
				} else {
					// 대표경력이 선택되면 다른 경력들의 대표경력 해제
					if (field === 'is_representative' && value === true) {
						return { ...career, is_representative: false }
					}
					return career
				}
			}),
		)
	}

	const handleAddCareer = () => {
		// ID 생성 로직 개선
		let newId = 1
		if (careers.length > 0) {
			const existingIds = careers
				.map((c) => c.id)
				.filter((id) => typeof id === 'number' && !isNaN(id))
			if (existingIds.length > 0) {
				newId = Math.max(...existingIds) + 1
			}
		}
		setCareers((prevCareers) => [
			...prevCareers,
			{
				id: newId,
				tournament_name: '',
				tournament_date: '',
				tournament_rank: '',
				league: '',
				is_representative: false,
			},
		])
	}

	const handleDeleteCareer = (id) => {
		if (careers.length > 1) {
			setCareers((prevCareers) => prevCareers.filter((career) => career.id !== id))
		}
	}

	const validateCareer = (career) => {
		const errors = []

		// 1. 대회명 5글자 이상 검사
		if (!career.tournament_name || career.tournament_name.length < 5) {
			errors.push('대회명은 5글자 이상 입력해주세요')
		}

		// 2. 대회일 유효성 검사
		if (!career.tournament_date) {
			errors.push('대회일을 선택해주세요')
		} else {
			const tournamentDate = new Date(career.tournament_date)
			const today = new Date()
			const minDate = new Date('1960-01-01') // 60년 이전

			if (tournamentDate > today) {
				errors.push('대회일은 오늘보다 미래일 수 없습니다')
			}
			if (tournamentDate < minDate) {
				errors.push('대회일이 너무 오래되었습니다')
			}
		}

		// 3. 순위 선택 검사
		if (!career.tournament_rank) {
			errors.push('순위를 선택해주세요')
		}

		// 4. 리그 선택 검사
		if (!career.league) {
			errors.push('리그를 선택해주세요')
		}

		return errors
	}

	const isFormValid = () => {
		// 경력이 1개도 차있지 않으면 false
		if (careers.length === 0) return false

		// 모든 경력이 유효한지 검사
		return careers.every((career) => {
			const errors = validateCareer(career)
			return errors.length === 0
		})
	}

	const handleFinishClick = () => {
		// 전체 유효성 검사
		const allErrors = []
		careers.forEach((career, index) => {
			const errors = validateCareer(career)
			if (errors.length > 0) {
				allErrors.push(`경력 ${index + 1}: ${errors.join(', ')}`)
			}
		})

		if (allErrors.length > 0) {
			setIsAlertMessageVisible(true)
			console.log('유효성 검사 실패:', allErrors)
			return
		}

		onFinish(careers)
	}

	const handleReturn = () => {
		onReturn()
	}

	const rankOptions = [
		{ value: 'FIRST', label: '1위' },
		{ value: 'SECOND', label: '2위' },
		{ value: 'THIRD', label: '3위' },
		{ value: 'FOURTH', label: '4위' },
		{ value: 'FIFTH', label: '5위' },
		{ value: 'SIXTH', label: '6위' },
		{ value: 'SEVENTH', label: '7위' },
		{ value: 'EIGHTH', label: '8위' },
		{ value: 'NINTH', label: '9위' },
		{ value: 'TENTH', label: '10위' },
	]

	const leagueOptions = [
		{ value: 'KPGA', label: 'KPGA' },
		{ value: 'KLPGA', label: 'KLPGA' },
		{ value: 'OTHER', label: '기타' },
	]

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				<div className={styles.modalHeader}>
					<h3>주요 경력사항</h3>
					<p>대회 성과를 입력해주세요</p>
				</div>

				<div className={styles.careerList}>
					{careers.map((career, index) => (
						<div key={career.id} className={styles.careerItem}>
							<div className={styles.careerHeader}>
								<span className={styles.careerNumber}>경력 {index + 1}</span>
								{index > 0 && (
									<button
										type="button"
										className={styles.deleteButton}
										onClick={() => handleDeleteCareer(career.id)}
										disabled={careers.length === 1}>
										<TrashIcon className={styles.trashIcon} />
									</button>
								)}
							</div>

							<div className={styles.inputGroup}>
								<div className={styles.inputField}>
									<label>대회명</label>
									<input
										type="text"
										placeholder="대회명 입력"
										value={career.tournament_name}
										onChange={(e) =>
											handleCareerChange(
												career.id,
												'tournament_name',
												e.target.value,
											)
										}
									/>
								</div>

								<div className={styles.inputField}>
									<label>대회일</label>
									<input
										type="date"
										value={career.tournament_date}
										onChange={(e) =>
											handleCareerChange(
												career.id,
												'tournament_date',
												e.target.value,
											)
										}
										placeholder="YYYY-MM-DD"
									/>
								</div>
							</div>

							<div className={styles.inputGroup}>
								<div className={styles.inputField}>
									<label>순위</label>
									<Select
										value={
											rankOptions.find(
												(option) => option.value === career.tournament_rank,
											) || null
										}
										onChange={(selectedOption) =>
											handleCareerChange(
												career.id,
												'tournament_rank',
												selectedOption?.value || '',
											)
										}
										options={rankOptions}
										placeholder="순위 선택"
										isSearchable={false}
										classNamePrefix="custom-select"
									/>
								</div>

								<div className={styles.inputField}>
									<label>리그</label>
									<Select
										value={
											leagueOptions.find(
												(option) => option.value === career.league,
											) || null
										}
										onChange={(selectedOption) =>
											handleCareerChange(
												career.id,
												'league',
												selectedOption?.value || '',
											)
										}
										options={leagueOptions}
										placeholder="리그 선택"
										isSearchable={false}
										classNamePrefix="custom-select"
									/>
								</div>
							</div>

							<div className={styles.checkboxField}>
								<label className={styles.checkboxLabel}>
									<input
										type="checkbox"
										checked={career.is_representative}
										onChange={(e) =>
											handleCareerChange(
												career.id,
												'is_representative',
												e.target.checked,
											)
										}
										className={styles.customCheckbox}
									/>
									대표 경력으로 등록할래요
								</label>
							</div>
						</div>
					))}
				</div>

				<div className={styles.addButton} onClick={handleAddCareer}>
					<PlusIcon className={styles.plusIcon} />
					<span>경력 추가하기</span>
				</div>

				<div className={styles.modalFooter}>
					<button className={styles.cancelButton} onClick={handleReturn} type="button">
						취소
					</button>
					<button
						className={styles.finishButton}
						onClick={handleFinishClick}
						type="button"
						disabled={!isFormValid()}>
						완료
					</button>
				</div>
			</div>

			{isAlertMessageVisible && (
				<div className={styles.alertMessageBox}>
					<ExclamationCircleIcon className={styles.warningIcon} />
					<p>필수 항목이 누락되었어요.</p>
					<p>다시 입력해주세요.</p>
				</div>
			)}
		</div>
	)
}

export default CareerModal
